import React from "react";
import { FormikHelpers, FormikValues } from "formik";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import { trackEvent } from "services/analytics";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import UserForm from "pages/shared-forms/user";
import {
  cleanUserFormOutput,
  handleSubmitError,
  handleResponse,
  UserFormData,
} from "./shared-create-update";

type Actions = FormikHelpers<FormikValues>;

const gqlUserToOption = (x: { id: string; name: string }) => ({ id: x.id, label: x.name });

const USER = gql`
  query getUserforTeam($id: ID!) {
    user(id: $id) {
      id
      organizationId
      firstName
      lastName
      email
      roles
      directPhone
      title
      lists {
        id
        name
      }
    }
  }
`;

const UPDATE_USER_MUTATION = gql`
  mutation updateUserForTeam($id: ID!, $data: UserInput!) {
    updateUser(id: $id, data: $data) {
      id
      firstName
      lastName
      email
      roles
    }
  }
`;
interface Props {
  path: string;
  userId?: string;
}

const EditTeamMember: React.FC<Props> = (props: Props) => {
  const { loading, data, error } = useQuery(USER, {
    variables: { id: props.userId },
    fetchPolicy: "cache-and-network",
  });
  const [updateUser, updateUserMutation] =
    useMutation<{ res: { id: string } }>(UPDATE_USER_MUTATION);

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;

  const handleSubmit = async (values: UserFormData, actions: Actions) => {
    const userInput = cleanUserFormOutput(values);
    // @ts-ignore TODO fix ids passed in from GQL
    const mutationVars = { id: values.id, data: userInput };

    // Nested objects are not supported by Pendo.
    const fromData = Object.entries(data.user).reduce((acc, [key, val]) => (
      key === "lists" ? acc : { ...acc, [`from_${key}`]: val }), {});
    const toData = Object.entries(userInput).reduce((acc, [key, val]) => (
      key === "lists" ? acc : { ...acc, [`to_${key}`]: val }), {});

    trackEvent("manager:updated_user", { ...fromData, ...toData });

    await updateUser({ variables: mutationVars })
      // clear form and go to /team page
      .then(handleResponse(actions))
      .catch(handleSubmitError("UPDATE"));
  };

  const user = {
    ...data.user,
    lists: (data.user.lists || []).map(gqlUserToOption),
  };

  return (
    <div>
      <h2 className="margin-bottom-l">Update User</h2>

      <div>
        <UserForm
          isSending={updateUserMutation.loading}
          onSubmit={handleSubmit}
          initialData={user}
        />
      </div>
    </div>
  );
};


export default EditTeamMember;
